import Vue from 'vue';
import type { BudgetVariants, FzpReport, Utils } from '../types';


/**
 * Тип отображаемых отчетов.
 *
 * Возможные значения:
 *
 * * 'STAFFTAB' - Отчеты ШР;
 * * 'FZP' - Отчеты ФЗП.
 */
export type ShownReportType = 'STAFFTAB' | 'FZP';

/**
 * Типы для отчетов ФЗП
 */
export namespace Fzp {
    /**
     * Ответ на запрос списка отчетов ФЗП
     */
    export interface ReportListResponse {
        /**
         * Список отчетов ФЗП
         */
        reportList: Utils.PaginationList<FzpReport>;

        /**
         * Карта "UUID варианта бюджета - вариант бюджета"
         */
        budgetVariantMap: Record<string, BudgetVariants>;
    }

    /**
     * Наборы коллекций статусов отчетов.
     *
     * Возможные значения:
     *
     * * 'IN_PROGRESS' - В процессе обработки;
     * * 'EXPORTED' - Выгруженные.
     */
    export type StatusCollection = 'IN_PROGRESS' | 'EXPORTED';
}


/**
 * Контекст для отчетов (для хранения данных до перезагрузки страницы)
 */
export interface ReportRuntimeContext {
    /**
     * Тип отображаемых отчетов
     */
    shownReportType: ShownReportType;

    // region ФЗП
    /**
     * Набор коллекций статусов отчетов
     */
    fzpStatusCollection: Fzp.StatusCollection;

    /**
     * ID нового отчета ФЗП.
     * Изменяется, когда пользователь запускает генерацию отчета ФЗП (создает задачу генерации).
     *
     * Это значение нужно ТОЛЬКО для отслеживания события "пользователь запустил генерацию отчета ФЗП".
     */
    fzpNewReportId: number | null;
    // endregion
}


export const reportRuntimeContext: ReportRuntimeContext = ((): ReportRuntimeContext => {
    interface Data {
        /**
         * Тип отображаемых отчетов
         */
        shownReportType: ShownReportType;

        // region ФЗП
        /**
         * Набор коллекций статусов отчетов
         */
        fzpStatusCollection: Fzp.StatusCollection;

        /**
         * ID нового отчета ФЗП.
         * Изменяется, когда пользователь запускает генерацию отчета ФЗП (создает задачу генерации).
         *
         * Это значение нужно ТОЛЬКО для отслеживания события "пользователь запустил генерацию отчета ФЗП".
         */
        fzpNewReportId: number | null;
        // endregion
    }

    const data = Vue.observable<Data>({
        shownReportType: 'STAFFTAB',
        fzpStatusCollection: 'EXPORTED',
        fzpNewReportId: null,
    });

    return {
        get shownReportType(): ShownReportType {
            return data.shownReportType;
        },

        set shownReportType(value: ShownReportType) {
            data.shownReportType = value;
        },


        // region ФЗП
        get fzpStatusCollection(): Fzp.StatusCollection {
            return data.fzpStatusCollection;
        },

        set fzpStatusCollection(value: Fzp.StatusCollection) {
            data.fzpStatusCollection = value;
        },

        get fzpNewReportId(): number | null {
            return data.fzpNewReportId;
        },

        set fzpNewReportId(value: number | null) {
            data.fzpNewReportId = value;
        }
        // endregion
    };
})();